import React from 'react';
import moment from 'moment';
import { Grid, Button, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { AccountData } from '../../../../api';
import { useAuth } from '../../../../session/InternalAuthProvider';
import { useLanguage } from '../../../../common/GeneralUtils';
import { getTimeFormat } from '../../../../common/getDateAndTimeFormat';
import { customerFacingPriceFormatter } from '../../../../utils/utils';
import { DATE_FORMAT } from '../../../calendarWrapper/CalendarWrapper';
import { EditOrderFormValues } from '../EditOrderTypes';
import { EditOrderDialogIntent, getButtonText } from '../components/footer/EditOrderDialogFooter';
// @ts-ignore
import { useEditOrder } from '../EditOrderContext';
import { PaymentModel } from './components/paymentModel';
import { quantityLabel } from './components/quantityLabel';
import { WizardStep } from './wizardSteps';

const SUMMARY_SEPARATOR = ' · ';
const PAYMENT_KEY_PREFIX = 'calendar.create_order.payment';
const NOTICE_SX = { borderRadius: '6px', p: '10px 14px', fontSize: 14 };

type WizardFooterProps = {
  step: WizardStep;
  loading: boolean;
  editOrderDialogIntent: EditOrderDialogIntent;
  paymentModel: PaymentModel;
  // Set after a failed Create: the step to complete first.
  incompleteStep?: WizardStep;
  onBack: () => void;
  onNext: () => void;
  onCreate: () => void;
};

export const WizardFooter = ({
  step,
  loading,
  editOrderDialogIntent,
  paymentModel,
  incompleteStep,
  onBack,
  onNext,
  onCreate,
}: WizardFooterProps) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { values } = useFormikContext<EditOrderFormValues>();
  // @ts-ignore
  const { editOrderState } = useEditOrder();

  const dateLabel = !values.date
    ? undefined
    : values.date === moment().format(DATE_FORMAT)
    ? t('calendar.create_order.today')
    : moment(values.date).format('DD.MM');
  const priceSegment = () => {
    if (paymentModel === PaymentModel.PayAtVisit) {
      return t(`${PAYMENT_KEY_PREFIX}.atVisit`);
    }
    if (paymentModel === PaymentModel.Deposit) {
      return (
        <>
          <span dir="ltr">{customerFacingPriceFormatter(values.upfrontAmount, account)}</span>{' '}
          {t(`${PAYMENT_KEY_PREFIX}.deposit`)}
        </>
      );
    }
    return <span dir="ltr">{customerFacingPriceFormatter(editOrderState.details.price, account)}</span>;
  };

  const summaryParts: React.ReactNode[] = [
    editOrderState.orderPage.label[language],
    `${quantityLabel(editOrderState.orderPage, language, t('calendar.create_order.quantityFallback'))} ${
      editOrderState.details.persons
    }`,
    dateLabel,
    dateLabel && <span dir="ltr">{moment.unix(values.timeSeconds).tz('utc').format(getTimeFormat(account))}</span>,
    `${values.firstname} ${values.lastname}`.trim(),
    step === WizardStep.Extras && priceSegment(),
  ].filter(Boolean);

  return (
    <Grid
      container
      direction="column"
      flexWrap="nowrap"
      gap={1.5}
      sx={{
        flexShrink: 0,
        width: 'auto',
        p: { xs: '12px 12px 16px', md: '16px 24px 22px' },
        mx: { xs: '-12px', md: '-24px' },
        borderTop: '1px solid',
        borderColor: 'mb.lineSoft',
      }}>
      {incompleteStep && (
        <Grid
          container
          alignItems="center"
          wrap="nowrap"
          gap={1}
          sx={{ ...NOTICE_SX, bgcolor: 'error.light', color: 'error.main' }}>
          <WarningAmberOutlined sx={{ fontSize: 18 }} />
          {t('calendar.create_order.stepIncomplete', { step: incompleteStep })}
        </Grid>
      )}
      <Typography variant="caption" color="text.secondary">
        {summaryParts.map((part, index) => (
          <React.Fragment key={index}>
            {index > 0 && SUMMARY_SEPARATOR}
            {part}
          </React.Fragment>
        ))}
      </Typography>
      <Grid container wrap="nowrap" gap={1.5}>
        {step !== WizardStep.WhatWhen && (
          <Button variant="outlined" size="large" disabled={loading} onClick={onBack} sx={{ textTransform: 'none' }}>
            {t('calendar.create_order.back')}
          </Button>
        )}
        {step === WizardStep.Extras ? (
          <LoadingButton
            variant="contained"
            size="large"
            loading={loading}
            onClick={onCreate}
            sx={{ textTransform: 'none', flex: 1 }}>
            {getButtonText(editOrderDialogIntent, t)}
          </LoadingButton>
        ) : (
          <Button variant="contained" size="large" onClick={onNext} sx={{ textTransform: 'none', flex: 1 }}>
            {t('calendar.create_order.next')}
          </Button>
        )}
      </Grid>
    </Grid>
  );
};
